import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Activity, ShieldCheck, Gauge, Mail, KeyRound, Send, Check, Users, AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const err = (e, f) => e?.response?.data?.detail?.message || e?.response?.data?.detail || f;

const Stat = ({ label, value, note, colour }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
    <p className="text-xs text-slate-300">{label}</p>
    <p className="text-2xl text-white font-semibold mt-1 fp-num" style={{ color: colour }}>{value}</p>
    {note && <p className="text-xs text-slate-300 mt-1.5">{note}</p>}
  </div>
);

/** How the cohort is doing, who's blocking it, and what the AI behind it costs. */
export const ProgramDashboard = ({ orgId, accent = '#22d3ee', canManage }) => {
  const { getAuthHeader } = useAuth();
  const [d, setD] = useState(null);
  const [consentFor, setConsentFor] = useState(null);
  const [signedName, setSignedName] = useState('');
  const [method, setMethod] = useState('in_person');
  const [mailOpen, setMailOpen] = useState(false);
  const [audiences, setAudiences] = useState([]);
  const [mail, setMail] = useState({ audience: 'participants_without_logins', message: '', reset_passwords: true });
  const [sending, setSending] = useState(false);
  const [byok, setByok] = useState({ provider: 'openai', api_key: '' });

  const load = useCallback(() => {
    axios.get(`${API}/orgs/${orgId}/dashboard`, getAuthHeader())
      .then(r => setD(r.data)).catch(() => {});
    axios.get(`${API}/orgs/${orgId}/notify/audiences`, getAuthHeader())
      .then(r => setAudiences(r.data.audiences)).catch(() => {});
  }, [orgId, getAuthHeader]);
  useEffect(load, [load]);

  const recordConsent = async () => {
    try {
      const r = await axios.post(`${API}/orgs/${orgId}/consents/${consentFor.id}/record`,
        { signed_name: signedName, method }, getAuthHeader());
      toast.success(r.data.message);
      setConsentFor(null); setSignedName(''); load();
    } catch (e) { toast.error(err(e, 'Could not record that')); }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/orgs/${orgId}/notify`, mail, getAuthHeader());
      toast.success(r.data.message);
      setMailOpen(false); load();
    } catch (e) { toast.error(err(e, 'Could not send those')); }
    finally { setSending(false); }
  };

  const setBilling = async (mode) => {
    try {
      const r = await axios.put(`${API}/orgs/${orgId}/ai-billing`,
        mode === 'byok' ? { mode, ...byok } : { mode }, getAuthHeader());
      toast.success(r.data.message);
      setByok({ provider: 'openai', api_key: '' });
      load();
    } catch (e) { toast.error(err(e, 'Could not change that')); }
  };

  if (!d) return null;
  const u = d.ai_usage;
  const pending = d.consent_ledger.filter(c => c.status === 'pending');
  const signed = d.consent_ledger.filter(c => c.status === 'signed');

  return (
    <div className="space-y-6" data-testid="program-dashboard">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="On the roster" value={d.people.total} note={d.seats.label} />
        <Stat label="Started their FuturePath"
          value={`${d.people.progress.finished_welcome + d.people.progress.in_progress} of ${d.people.total}`}
          note={`${d.people.progress.finished_welcome} finished the welcome`} />
        <Stat label="Haven't signed in" value={d.people.total - d.people.signed_in - d.people.guardian_managed}
          note="a nudge email fixes most of these" colour="#fbbf24" />
        <Stat label="Waiting on consent" value={d.people.awaiting_consent}
          note={d.people.awaiting_consent ? 'nothing starts for them until it lands' : 'all clear'}
          colour={d.people.awaiting_consent ? '#fb7185' : '#34d399'} />
      </div>

      {/* Programs */}
      <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-5 md:p-6" data-testid="dashboard-programs">
        <div className="flex flex-wrap items-center gap-3">
          <Activity className="w-4 h-4" style={{ color: accent }} />
          <h2 className="font-outfit text-xl font-semibold text-white">How each program is doing</h2>
          {canManage && (
            <Button onClick={() => setMailOpen(true)} data-testid="bulk-email-btn"
              className="ml-auto rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Email the ones who are stuck
            </Button>
          )}
        </div>
        {d.programs.length === 0 && (
          <p className="text-sm text-slate-300 mt-4" data-testid="dashboard-no-programs">
            No programs yet, create one below and its progress shows up here.
          </p>
        )}
        <div className="grid md:grid-cols-2 gap-3 mt-5">
          {d.programs.map(p => {
            const t = Math.max(1, p.enrolled);
            const pct = (n) => `${(n / t) * 100}%`;
            return (
              <div key={p.id} className="rounded-xl border border-white/10 bg-slate-800/40 p-4"
                data-testid={`dashboard-program-${p.id}`}>
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium truncate">{p.name}</p>
                  <span className="text-xs text-slate-300 ml-auto shrink-0">
                    {p.enrolled}{p.capacity ? `/${p.capacity}` : ''} enrolled
                  </span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-slate-900 overflow-hidden flex">
                  <div style={{ width: pct(p.progress.finished_welcome), background: '#34d399' }} />
                  <div style={{ width: pct(p.progress.in_progress), background: accent }} />
                  <div style={{ width: pct(p.progress.not_started), background: '#334155' }} />
                </div>
                <div className="flex flex-wrap gap-3 mt-2.5 text-xs text-slate-300">
                  <span className="text-emerald-300">{p.progress.finished_welcome} finished</span>
                  <span style={{ color: accent }}>{p.progress.in_progress} in progress</span>
                  <span>{p.progress.not_started} not started</span>
                  {p.awaiting_consent > 0 && (
                    <span className="text-rose-300">{p.awaiting_consent} need consent</span>
                  )}
                </div>
                <p className="text-xs text-slate-300 mt-2 font-mono">{p.invite_code}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Consent ledger, built in */}
      <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-5 md:p-6" data-testid="consent-ledger">
        <div className="flex flex-wrap items-center gap-3">
          <ShieldCheck className="w-4 h-4 text-emerald-300" />
          <h2 className="font-outfit text-xl font-semibold text-white">Consent ledger</h2>
          <span className="text-xs text-slate-300">
            {pending.length} waiting · {signed.length} on file
          </span>
        </div>
        <p className="text-sm text-slate-300 mt-1.5">
          Who approved what, when, and who recorded it. Nothing starts for an under-13 until their
          guardian has said yes.
        </p>

        {pending.length > 0 && (
          <div className="mt-4 space-y-2">
            {pending.map(c => (
              <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-400/25 bg-amber-500/[0.07] px-4 py-2.5"
                data-testid={`consent-pending-${c.id}`}>
                <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                <span className="text-sm text-white">{c.participant}</span>
                <span className="text-xs text-slate-300">{c.age}</span>
                <span className="text-xs text-slate-300">
                  {c.guardian ? `${c.guardian} · ${c.guardian_email}` : 'no guardian on file'}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-amber-300/90">
                  {(c.reason || '').replace(/_/g, ' ')}
                </span>
                {c.requested_at && (
                  <span className="text-xs text-slate-300">
                    asked {String(c.requested_at).slice(0, 10)}
                  </span>
                )}
                {canManage && (
                  <Button onClick={() => { setConsentFor(c); setSignedName(c.guardian || ''); }}
                    data-testid={`record-consent-${c.id}`} size="sm"
                    className="ml-auto rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                    Record their consent
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {signed.length > 0 && (
          <div className="mt-4 space-y-1.5" data-testid="consent-signed-list">
            {signed.slice(0, 12).map((c, i) => (
              <div key={c.id || i} className="flex flex-wrap items-center gap-3 text-xs text-slate-300">
                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span className="text-slate-200">{c.participant}</span>
                <span>signed by {c.guardian || '-'}</span>
                <span>{String(c.signed_at || '').slice(0, 10)}</span>
                {c.expires_at && <span>expires {String(c.expires_at).slice(0, 10)}</span>}
                {c.version && <span className="text-slate-300">{c.version}</span>}
              </div>
            ))}
          </div>
        )}
        {pending.length === 0 && signed.length === 0 && (
          <p className="text-sm text-slate-300 mt-4" data-testid="consent-empty">
            Nothing to approve yet. Consent requests appear here the moment an under-13 is added.
          </p>
        )}
      </div>

      {/* AI usage + bring your own key */}
      <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-5 md:p-6" data-testid="ai-usage-panel">
        <div className="flex flex-wrap items-center gap-3">
          <Gauge className="w-4 h-4 text-violet-300" />
          <h2 className="font-outfit text-xl font-semibold text-white">What the AI is costing</h2>
          <span className="text-xs text-slate-300">last {u.window_days} days</span>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ml-auto"
            style={{ background: u.byok ? '#34d39922' : '#a78bfa22', color: u.byok ? '#34d399' : '#a78bfa' }}
            data-testid="ai-mode-badge">
            {u.byok ? `your own ${u.provider} key ····${u.key_last4}` : 'included with your seats'}
          </span>
        </div>

        {!u.byok && (
          <>
            <div className="mt-4 h-2 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full rounded-full transition-all"
                style={{ width: `${Math.min(100, u.percent)}%`,
                  background: u.percent > 85 ? '#fb7185' : 'linear-gradient(90deg,#22d3ee,#a78bfa)' }} />
            </div>
            <p className="text-xs text-slate-300 mt-2" data-testid="ai-usage-line">
              {u.used.toLocaleString()} of {u.allowance.toLocaleString()} used · {u.percent}%
              {u.per_seat ? ` · ${u.per_seat} per place` : ''}
            </p>
          </>
        )}

        <div className="grid md:grid-cols-2 gap-5 mt-5">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-300">Where it goes</p>
            <div className="mt-2 space-y-1.5">
              {u.by_action.length === 0 && <p className="text-sm text-slate-300">Nothing used yet.</p>}
              {u.by_action.slice(0, 6).map((a, i) => (
                <div key={i} className="flex items-center gap-3 text-xs text-slate-300">
                  <span className="text-slate-200 truncate">{a.action}</span>
                  <span className="ml-auto fp-num">{a.credits}</span>
                  <span className="text-slate-300">{a.requests} runs</span>
                  {a.cost_usd != null && <span className="text-slate-300">${a.cost_usd}</span>}
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-300">Busiest participants</p>
            <div className="mt-2 space-y-1.5">
              {u.top_participants.length === 0 && <p className="text-sm text-slate-300">Nothing yet.</p>}
              {u.top_participants.map((p, i) => (
                <div key={i} className="flex items-center gap-3 text-xs text-slate-300">
                  <Users className="w-3 h-3 shrink-0" />
                  <span className="text-slate-200 truncate">{p.name}</span>
                  <span className="ml-auto fp-num">{p.credits}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {canManage && (
          <div className="mt-6 rounded-xl border border-white/10 bg-slate-800/40 p-4" data-testid="byok-panel">
            <div className="flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5 text-emerald-300" />
              <p className="text-sm text-white font-medium">Bring your own AI key</p>
            </div>
            <p className="text-xs text-slate-300 mt-1.5 max-w-2xl">
              Run the AI on your own provider account instead of the allowance included with your
              seats. Usage is then billed to you by the provider, we still show you exactly what's
              being spent. Your key is stored apart from your organization record and never shown back.
            </p>
            {u.byok ? (
              <Button onClick={() => setBilling('platform')} data-testid="byok-off"
                className="mt-3 rounded-full bg-slate-700 hover:bg-slate-600 text-white text-sm">
                Go back to the allowance with my seats
              </Button>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3 mt-3 items-end">
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">Provider</Label>
                  <Select value={byok.provider} onValueChange={v => setByok({ ...byok, provider: v })}>
                    <SelectTrigger className="input-dark" data-testid="byok-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['openai', 'anthropic', 'gemini'].map(p =>
                        <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">API key</Label>
                  <Input type="password" value={byok.api_key}
                    onChange={e => setByok({ ...byok, api_key: e.target.value })}
                    className="input-dark" data-testid="byok-key" />
                </div>
                <Button onClick={() => setBilling('byok')} disabled={byok.api_key.length < 20}
                  data-testid="byok-save"
                  className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                  Use my key
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Record consent */}
      <Dialog open={!!consentFor} onOpenChange={(v) => !v && setConsentFor(null)}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit text-lg">
              Consent for {consentFor?.participant}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-300">
            Record what the guardian has actually agreed to. Your name goes on the record alongside
            theirs, and {consentFor?.participant?.split(' ')[0]} can start straight away.
          </p>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Guardian's full name</Label>
            <Input value={signedName} onChange={e => setSignedName(e.target.value)}
              className="input-dark" data-testid="consent-signed-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">How they gave it</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="input-dark" data-testid="consent-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in_person">In person</SelectItem>
                <SelectItem value="phone">Over the phone</SelectItem>
                <SelectItem value="paper_form">Signed paper form</SelectItem>
                <SelectItem value="document_on_file">Document already on file</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={recordConsent} disabled={signedName.trim().length < 3}
            data-testid="consent-record-save"
            className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
            Record it
          </Button>
        </DialogContent>
      </Dialog>

      {/* Bulk email */}
      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit text-lg">Send one email to everyone</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Who gets it</Label>
            <Select value={mail.audience} onValueChange={v => setMail({ ...mail, audience: v })}>
              <SelectTrigger className="input-dark" data-testid="mail-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                {audiences.map(a => (
                  <SelectItem key={a.key} value={a.key}>{a.label} ({a.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Add a line of your own (optional)</Label>
            <Textarea value={mail.message} onChange={e => setMail({ ...mail, message: e.target.value })}
              className="input-dark min-h-[80px]" data-testid="mail-message"
              placeholder="We start Monday at 9, bring a notebook." />
          </div>
          {mail.audience.startsWith('participants') && (
            <label className="flex items-start gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={mail.reset_passwords} data-testid="mail-reset"
                onChange={e => setMail({ ...mail, reset_passwords: e.target.checked })}
                className="mt-0.5" />
              Include a fresh starting password for anyone who hasn't signed in yet
            </label>
          )}
          <Button onClick={send} disabled={sending} data-testid="mail-send"
            className="rounded-full text-slate-900 font-semibold" style={{ background: accent }}>
            <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Sending…' : 'Send it'}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramDashboard;
